import styled, { css } from 'styled-components';
import { COLORS } from '..';
import { scrollBarStyling } from '../styles';
import { Size } from '../types';

/**
 * Modal styles
 */
export const ModalHeader = styled.section<{ marginBottom?: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) => props.marginBottom || '24px'};
`;

export const StyledModalHeader = styled(ModalHeader)<{ marginBottom?: string; size?: string; $height?: number }>`
  flex: 1 0 calc(50% - 5px);
  flex-direction: column;
  flex-basis: auto;
  text-align: left;
  height: ${(props) => (props.$height ? `${props.$height - (props.size === 'small' ? 16 : props.size === 'large' ? 32 : 24)}px` : '')};
  img {
    width: 100%;
    height: ${(props) => `${props.$height}px`};
    object-fit: cover;
    border-radius: 8px 8px 0 0;
  }
`;

export const ImageContainer = styled.div<{ $height: number }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: ${(props) => `${props.$height}px`};

  #imgWithFallbackLoadingIndicator {
    position: absolute;
    left: 50%;
    top: 0;
  }
`;

export const ImageOverlay = styled.div<{ $height: number }>`
  position: absolute;
  content: '';
  top: 0;
  left: 0;
  height: ${(props) => `${props.$height}px`};
  width: 100%;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 8px 8px 0 0;

  :after {
    position: absolute;
    height: ${(props) => `${props.$height}px`};
    border-radius: 8px 8px 0 0;
    content: '';
    top: 0;
    left: 0;
    width: 100%;
    background: #888888;
    mix-blend-mode: saturation;
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  height: 100%;
`;

export const ModalTitleSection = styled.section`
  word-break: break-word;
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

export const ModalHeaderActionsWithImage = styled.div<{ hasBackButton?: boolean }>`
  display: flex;
  flex-direction: ${(props) => (!props.hasBackButton ? 'row-reverse' : 'row')};
  justify-content: space-between;
  width: 100%;
  z-index: 1;
`;

export const ModalHeaderActions = styled.div`
  display: flex;
  margin: -12px -12px 0 0;
`;

export const FlexContainer = styled.div`
  display: flex;
  width: 100%;
`;

export const ModalHoverModifier = styled.div`
  button:hover {
    cursor: help !important;
  }
`;

export const CloseButtonWrapper = styled.div<{ size?: Size; hasImage?: boolean }>`
  button {
    ${(props) =>
      props.hasImage
        ? css`
            margin: -12px -12px 0 0 !important;
          `
        : css`
            left: unset;
          `}
  }
`;

export const BackButtonWrapper = styled.div<{ size?: Size; hasImage?: boolean }>`
  button {
    margin: -12px 0 0 -12px !important;
  }
`;

export const ModalTitle = styled.h5<{ fontSize?: string }>`
  margin: 0;
  font-size: ${(props) => props.fontSize || '1.25em'};
  font-weight: bold;
`;

export const ModalNoteSection = styled.section<{ state: string; size?: string }>`
  background-color: ${(props) => getBackgroundColor(props.state)};
  font-size: ${(props) => (props?.size === 'small' ? `16px` : props?.size === 'large' ? '20px' : '18px')};
  margin-top: ${(props) => (props?.size === 'small' ? `0px` : props?.size === 'large' ? '16px' : '4px')};
  margin-bottom: ${(props) => (props?.size === 'small' ? `8px` : props?.size === 'large' ? '24px' : '16px')};
  display: flex;
  flex-direction: row;
  padding: ${(props) => (props?.size === 'small' ? `8px` : props?.size === 'large' ? '16px' : '12px')};
  border-radius: 2px;

  span {
    display: block;
    width: calc(100% - 32px);
    margin-left: 8px;
    color: ${(props) => getNoteMessageColor(props.state)};
  }
`;

export const ModalBody = styled.section<{ size?: string; overflow?: string; hasContentBorders?: boolean }>`
  flex: 1 1 auto;
  overflow: ${(props) => props?.overflow ?? 'unset'};
  margin: ${(props) => (props?.size === 'small' ? `8px 0` : props?.size === 'large' ? '16px 0' : '12px 0')};

  border-top: ${(props) => (props?.hasContentBorders ? `1px solid ${COLORS.neutral_100}` : 'none')};
  border-bottom: ${(props) => (props?.hasContentBorders ? `1px solid ${COLORS.neutral_100}` : 'none')};

  ${scrollBarStyling(Size.Small)}
`;

export const ModalFooter = styled.section<{ size?: Size }>`
  display: flex;
  justify-content: flex-end;

  gap: ${(props) => (props?.size === 'large' ? `16px` : '8px')};

  .footer-action {
    order: -1;
    margin-right: auto;
    display: flex;
    align-items: center;
    font-size: ${(props) => (props?.size === 'small' ? `16px` : props?.size === 'large' ? '20px' : '18px')};
    gap: ${(props) => (props?.size === Size.Large ? `8px` : props?.size === Size.Small ? '4px' : '6px')};
  }

  .footer-action.note {
    color: ${COLORS.neutral_600};
  }
`;

export const getBackgroundColor = (state: string) => {
  switch (state) {
    case 'positive':
      return COLORS.correct_100;
    case 'warning':
      return COLORS.warning_100;
    case 'critical':
      return COLORS.critical_100;
    default:
      return COLORS.primary_100;
  }
};

export const getNoteMessageColor = (state: string) => {
  switch (state) {
    case 'positive':
      return COLORS.correct_700;
    case 'warning':
      return COLORS.warning_700;
    case 'critical':
      return COLORS.critical_700;
    default:
      return COLORS.primary_700;
  }
};
